import React, { useEffect, useState } from 'react';
import { Card, Col, Button, Form, Alert, Spinner, Image } from 'react-bootstrap';
import logolight from "assets/images/mayaveLogo.svg";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { loginUser, resetLoginFlag } from "../../slices/thunk";
import { createSelector } from 'reselect';
import Loader from 'Common/Loader';
import ParticlesAuth from 'pages/AuthenticationInner/ParticlesAuth';
import { logoutUserSuccess } from 'slices/auth/login/reducer';
import { setAuthorization } from 'helpers/api_helper';

const Signin = () => {
    const navigate = useNavigate();
    const dispatch: any = useDispatch();
    const [loader, setLoader] = useState(false);
    const [passwordShow, setPasswordShow] = useState(false);

    const selectAccountAndLogin = createSelector(
        (state: any) => state.Account,
        (state: any) => state.Login,
        (account, login) => ({
            user: account?.user,
            error: login.error,
            loading: login.loading,
            errorMsg: login.errorMsg,
        })
    );

    const { user, error, loading, errorMsg } = useSelector(selectAccountAndLogin);

    const initialUserName = user?.userName || "";
    const initialPassword = user?.password || "";


    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            userName: initialUserName || "",
            password: initialPassword || "",
        },
        validationSchema: Yup.object({
            userName: Yup.string()
                .required("Please enter your user name")
                .matches(/^(?!\s*$).+$/, "User name cannot be only spaces"),
            password: Yup.string()
                .required("Please enter your password")
                .matches(/^(?!\s*$).+$/, "Password cannot be only spaces"),
        }),

        onSubmit: async (values) => {
            setLoader(true);
            const payload = {
                userName: values.userName.trim(),
                password: values.password.trim(),
            };
            try {
                const result = await dispatch(loginUser(payload, navigate));
                if (result?.type === "LOGIN_SUCCESS" || result?.payload?.success) {
                    const token = result?.payload?.token;
                    if (token) {
                        setAuthorization(token);
                    }
                    navigate("/dashboard");
                } else {
                    console.error("Login failed: ", result?.payload?.message);
                }
            } catch (err) {
                console.error("Login error: ", err);
            } finally {
                setLoader(false);
            }
        }
    });

    useEffect(() => {
        if (errorMsg) {
            const timer = setTimeout(() => {
                dispatch(resetLoginFlag());
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [dispatch, errorMsg]);

    useEffect(() => {
        dispatch(logoutUserSuccess({}));
        localStorage.clear()
    }, [])

    return (
        <ParticlesAuth>
            <>
                {loader && <Loader />}

                <Col xxl="6" lg="6" md="6" className="mx-auto">
                    <Card className="shadow-none">
                        <Card.Body className="p-sm-5 m-lg-4">
                            <div className="text-center">
                                <Image src={logolight} />
                                <h3 className="text-pantone mt-4">
                                    Welcome to Mayave !
                                </h3>
                                <h4 className="text-dark opacity-75 fs-base">
                                    Start your journey with us.
                                </h4>
                            </div>

                            <div className="p-2 mt-5">
                                {error && <Alert variant="danger">{error}</Alert>}

                                <Form onSubmit={validation.handleSubmit}>
                                    <Form.Group className="mb-3" controlId="formUsername">
                                        <Form.Label>Username <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            name="userName"
                                            className="form-control"
                                            placeholder="Enter username"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.userName}
                                            isInvalid={validation.touched.userName && !!validation.errors.userName}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {String(validation.errors.userName)}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formPassword">
                                        <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                                        <div className="position-relative auth-pass-inputgroup mb-3">
                                            <Form.Control
                                                type={passwordShow ? "text" : "password"}
                                                className="form-control pe-5"
                                                placeholder="Enter password"
                                                name="password"
                                                value={validation.values.password}
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                isInvalid={validation.touched.password && !!validation.errors.password}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {String(validation.errors.password)}
                                            </Form.Control.Feedback>

                                            <Button
                                                variant="link"
                                                className="position-absolute end-0 top-0 text-decoration-none text-muted"
                                                type="button"
                                                onClick={() => setPasswordShow(!passwordShow)}
                                            >
                                                <i className="ri-eye-fill align-middle"></i>
                                            </Button>
                                        </div>
                                    </Form.Group>

                                    <Form.Group controlId="formRememberMe">
                                        <Form.Check type="checkbox" label="Remember me" id="auth-remember-check" />
                                    </Form.Group>

                                    <div className="mt-4">
                                        <Button
                                            className="btn btn-primary bg-pantone w-100 rounded-2 py-2"
                                            type="submit"
                                            disabled={loading}
                                        >
                                            {loading && <Spinner size="sm" />} {" "}Sign In
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </>
        </ParticlesAuth>
    );
};

export default Signin;
